import { useCallback } from 'react';

import Constants from '@/Constants';
import { makeRequest } from '@/services/api';
import useStore from '@/store';

type CachedRequest = {
  promise: Promise<void>;
  timestamp: number;
};

const requestCache = new Map<string, CachedRequest>();
const DEDUPLICATION_WINDOW = 1000;

const useCustomServerTimeSync = () => {
  const setServerTime = useStore((state) => state.setServerTime);
  const setShowLoader = useStore((state) => state.setShowLoader);
  const loaderCount = useStore((state) => state.loaderCount);

  const syncServerTime = useCallback(async () => {
    const now = Date.now();
    const cached = requestCache.get(Constants.GET_SERVER_TIME_URL);

    if (cached && now - cached.timestamp < DEDUPLICATION_WINDOW) {
      return cached.promise;
    }

    setShowLoader(true);

    const fetchPromise = makeRequest<{ serverTime: string }>(Constants.GET_SERVER_TIME_URL, {}, {})
      .then((result) => {
        if (result.data?.serverTime) {
          setServerTime(result.data.serverTime);
        }
      })
      .catch((error) => {
        console.error('Failed to sync server time:', error);
      })
      .finally(() => {
        setShowLoader(false);
        setTimeout(() => {
          requestCache.delete(Constants.GET_SERVER_TIME_URL);
        }, DEDUPLICATION_WINDOW);
      });

    requestCache.set(Constants.GET_SERVER_TIME_URL, { promise: fetchPromise, timestamp: now });
    return fetchPromise;
  }, [setServerTime, setShowLoader]);

  return { syncServerTime, isLoading: loaderCount > 0 };
};

export default useCustomServerTimeSync;
